import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ErrorNotice, Field, useAction } from "../components/ui";
import { api, type Activity, type ActivityKind, type Company, type Contact, type ContactFields, type Reminder } from "../lib/api";
import { addDays, todayInLondon } from "../lib/dates";
import { ReminderGroup } from "./Today";

const pad = (n: number) => String(n).padStart(2, "0");
const when = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
export const KIND: Record<ActivityKind, string> = { call: "Call", email: "Email", meeting: "Meeting", note: "Note" };
export const KIND_MARK: Record<ActivityKind, string> = { call: "☎", email: "✉", meeting: "◎", note: "✎" };

/**
 * The activity timeline (first, as it's used most) and contacts for a company on this agency's
 * list. Anything passed as children sits between the two.
 */
export function CompanyCrm({ company, children }: { company: Company; children?: ReactNode }) {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [activity, setActivity] = useState<Activity[]>([]);
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = useAction();

  const reload = useCallback(
    () =>
      load.run(async () => {
        const [c, a] = await Promise.all([api.contacts(company.id), api.activity(company.id)]);
        setContacts(c.contacts);
        setActivity(a.activity);
        setReminders(a.reminders);
        setLoaded(true);
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [company.id],
  );
  useEffect(() => void reload(), [reload]);

  return (
    <>
      <ErrorNotice error={load.error} />
      <ActivitySection companyId={company.id} contacts={contacts} activity={activity} loaded={loaded} reminders={reminders} onChange={() => void reload()} />
      {children}
      <Contacts companyId={company.id} contacts={contacts} loaded={loaded} onChange={() => void reload()} />
    </>
  );
}

// Contacts -------------------------------------------------------------------------------------

function Contacts({ companyId, contacts, loaded, onChange }: { companyId: string; contacts: Contact[]; loaded: boolean; onChange: () => void }) {
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [confirmErase, setConfirmErase] = useState<string | null>(null);
  const { pending, error, run } = useAction();

  const act = (fn: () => Promise<unknown>) =>
    void run(async () => {
      await fn();
      setConfirmErase(null);
      onChange();
    });

  return (
    <section className="section" aria-labelledby="contacts-h">
      <div className="section-head">
        <h2 id="contacts-h" className="label">
          Contacts
        </h2>
        <button type="button" className="link-btn" onClick={() => setAdding((v) => !v)}>
          {adding ? "Cancel" : "+ Add contact"}
        </button>
      </div>
      {adding && (
        <ContactForm
          submitLabel="Add contact"
          onSubmit={async (fields) => {
            await api.addContact(companyId, fields);
            setAdding(false);
            onChange();
          }}
        />
      )}
      <ErrorNotice error={error} />
      <ol className="index">
        {contacts.map((c, i) =>
          editing === c.id ? (
            <li key={c.id} className="editing">
              <ContactForm
                initial={c}
                submitLabel="Save"
                onCancel={() => setEditing(null)}
                onSubmit={async (fields) => {
                  await api.updateContact(c.id, fields);
                  setEditing(null);
                  onChange();
                }}
              />
            </li>
          ) : (
            <li key={c.id} className={c.archived ? "inactive" : undefined}>
              <span className="num" aria-hidden>
                {pad(i + 1)}
              </span>
              <span className="main">
                <span className="primary">
                  {c.fullName}
                  {c.jobTitle && <span className="secondary"> · {c.jobTitle}</span>}
                  {c.archived && <span className="label"> · left</span>}
                </span>
                <span className="secondary contact-links">
                  {c.email && <a href={`mailto:${c.email}`}>{c.email}</a>}
                  {c.phone && <a href={`tel:${c.phone.replace(/[^\d+]/g, "")}`} className="figures">{c.phone}</a>}
                  {c.linkedinUrl && (
                    <a href={c.linkedinUrl} target="_blank" rel="noopener noreferrer">
                      LinkedIn ↗
                    </a>
                  )}
                </span>
              </span>
              <span className="actions">
                {confirmErase === c.id ? (
                  <>
                    <span className="label alert">Erase {c.fullName} and their history?</span>
                    <button type="button" className="link-btn danger" disabled={pending} onClick={() => act(() => api.eraseContact(c.id))}>
                      Erase permanently
                    </button>
                    <button type="button" className="link-btn" onClick={() => setConfirmErase(null)}>
                      Keep
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="link-btn" onClick={() => setEditing(c.id)}>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="link-btn"
                      disabled={pending}
                      onClick={() => act(() => api.updateContact(c.id, { archived: !c.archived }))}
                    >
                      {c.archived ? "Restore" : "Mark as left"}
                    </button>
                    <button type="button" className="link-btn danger" onClick={() => setConfirmErase(c.id)}>
                      Erase
                    </button>
                  </>
                )}
              </span>
            </li>
          ),
        )}
        {loaded && contacts.length === 0 && !adding && (
          <li>
            <span className="empty">No contacts yet. Add the people you're selling to here.</span>
          </li>
        )}
      </ol>
    </section>
  );
}

function ContactForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial?: Contact;
  submitLabel: string;
  onSubmit: (fields: ContactFields) => Promise<void>;
  onCancel?: () => void;
}) {
  const { pending, error, run } = useAction();
  const [f, setF] = useState({
    fullName: initial?.fullName ?? "",
    jobTitle: initial?.jobTitle ?? "",
    email: initial?.email ?? "",
    phone: initial?.phone ?? "",
    linkedinUrl: initial?.linkedinUrl ?? "",
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((v) => ({ ...v, [k]: e.target.value }));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void run(() =>
      onSubmit({
        fullName: f.fullName.trim(),
        jobTitle: f.jobTitle.trim() || null,
        email: f.email.trim() || null,
        phone: f.phone.trim() || null,
        linkedinUrl: f.linkedinUrl.trim() || null,
      }),
    );
  };

  return (
    <form className="form panel" onSubmit={submit}>
      <div className="grid-2">
        <Field label="Name" required maxLength={200} autoFocus value={f.fullName} onChange={set("fullName")} />
        <Field label="Job title" maxLength={200} value={f.jobTitle} onChange={set("jobTitle")} placeholder="e.g. Head of Talent" />
        <Field label="Email" type="email" value={f.email} onChange={set("email")} />
        <Field label="Phone" type="tel" value={f.phone} onChange={set("phone")} />
        <Field
          label="LinkedIn"
          type="url"
          value={f.linkedinUrl}
          onChange={set("linkedinUrl")}
          placeholder="https://www.linkedin.com/in/…"
        />
      </div>
      <ErrorNotice error={error} />
      <div className="button-row">
        <button className="btn" disabled={pending}>
          {pending ? "Saving…" : submitLabel}
        </button>
        {onCancel && (
          <button type="button" className="link-btn" onClick={onCancel}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

// Activity -------------------------------------------------------------------------------------

function ActivitySection({
  companyId,
  contacts,
  activity,
  reminders,
  loaded,
  onChange,
}: {
  companyId: string;
  contacts: Contact[];
  activity: Activity[];
  reminders: Reminder[];
  loaded: boolean;
  onChange: () => void;
}) {
  const today = todayInLondon();
  const log = useAction();
  const act = useAction();
  const [kind, setKind] = useState<ActivityKind>("call");
  const [summary, setSummary] = useState("");
  const [contactId, setContactId] = useState("");
  const [followUp, setFollowUp] = useState("");

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void log.run(async () => {
      await api.logActivity(companyId, {
        kind,
        summary: summary.trim(),
        contactId: contactId || null,
        followUp: followUp ? { dueOn: followUp } : undefined,
      });
      setSummary("");
      setFollowUp("");
      onChange();
    });
  };

  const updateReminder = (r: Reminder, change: { done?: boolean; dueOn?: string }) =>
    void act.run(async () => {
      await api.updateReminder(r.id, change);
      onChange();
    });

  return (
    <>
      {reminders.length > 0 && (
        <ReminderGroup
          title="Follow-ups"
          reminders={reminders}
          today={today}
          team
          onUpdate={updateReminder}
          busy={act.pending}
          showCompany={false}
        />
      )}

      <section className="section" aria-labelledby="activity-h">
        <div className="section-head">
          <h2 id="activity-h" className="label">
            Activity
          </h2>
          <span className="label figures">{pad(activity.length)}</span>
        </div>

        <form className="form log-form" onSubmit={submit}>
          <fieldset className="segmented">
            <legend className="sr-only">Type</legend>
            {(Object.keys(KIND) as ActivityKind[]).map((k) => (
              <label key={k}>
                <input type="radio" name="kind" checked={kind === k} onChange={() => setKind(k)} />
                <span>{KIND[k]}</span>
              </label>
            ))}
          </fieldset>
          <label className="field">
            <span className="label">What happened</span>
            <textarea rows={3} required maxLength={5000} value={summary} onChange={(e) => setSummary(e.target.value)} />
          </label>
          <div className="grid-2">
            <label className="field">
              <span className="label">With</span>
              <select value={contactId} onChange={(e) => setContactId(e.target.value)}>
                <option value="">— No one specific —</option>
                {contacts
                  .filter((c) => !c.archived)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.fullName}
                    </option>
                  ))}
              </select>
            </label>
            <div className="field">
              <label className="label" htmlFor="follow-up">
                Follow up on
              </label>
              <input id="follow-up" type="date" min={today} value={followUp} onChange={(e) => setFollowUp(e.target.value)} />
              <span className="quick-dates">
                {[
                  ["Tomorrow", 1],
                  ["In 3 days", 3],
                  ["Next week", 7],
                ].map(([label, days]) => (
                  <button key={label} type="button" className="link-btn" onClick={() => setFollowUp(addDays(today, days as number))}>
                    {label}
                  </button>
                ))}
              </span>
            </div>
          </div>
          <ErrorNotice error={log.error ?? act.error} />
          <div className="button-row">
            <button className="btn" disabled={log.pending || !summary.trim()}>
              {log.pending ? "Logging…" : `Log ${KIND[kind].toLowerCase()}`}
            </button>
          </div>
        </form>

        <ol className="index timeline">
          {activity.map((a) => (
            <li key={a.id}>
              <span className="num kind" aria-hidden>
                {KIND_MARK[a.kind]}
              </span>
              <span className="main">
                <span className="primary prewrap">{a.summary}</span>
                <span className="secondary">
                  <span className="sr-only">{KIND[a.kind]} </span>
                  {a.contact && <>{a.contact.fullName} · </>}
                  {a.createdBy ?? "a former teammate"} · <span className="figures">{when.format(new Date(a.occurredAt))}</span>
                </span>
              </span>
              <span className="actions" />
            </li>
          ))}
          {loaded && activity.length === 0 && (
            <li>
              <span className="empty">Nothing logged yet.</span>
            </li>
          )}
        </ol>
      </section>
    </>
  );
}
